import React, { useEffect, memo, useCallback, useRef } from 'react';
import { APIProvider, useMap, Map as GMap } from '@vis.gl/react-google-maps';
import './GoogleMap.scss';
import Marker from './Marker';
import Polyline from './Polyline';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setShouldFitbounds } from '../../redux/slice';

// A single bad coordinate (NaN/null/undefined — e.g. a route step whose order
// was removed, leaving the marker without lat/lng) makes Google's
// <gmp-advanced-marker> throw an uncaught "lat: NaN is not an accepted value"
// and take down the whole map. Guard every lat/lng we feed the map.
// Note: Number(null) === 0 and Number('') === 0, so reject null/undefined/''
// explicitly — otherwise a missing coord would slip through as a marker at 0,0.
const isFiniteNum = (v) => v != null && v !== '' && Number.isFinite(Number(v));
const validLatLng = (lat, lng) => isFiniteNum(lat) && isFiniteNum(lng);

// Fit-bounds must survive a stray/garbage coordinate — a 0,0 sentinel from a
// null-coord order, or a mis-geocoded point on another continent. Left
// unchecked, ONE bad point blows the map bounds out to the whole world — both
// on load AND on every "reset view" click (which faithfully re-fits the same
// bad bounds, so the reset looks broken). Note Number(null)===0, so 0,0 passes
// validLatLng — reject it explicitly, along with out-of-range values.
const plausibleLatLng = (lat, lng) =>
  validLatLng(lat, lng) &&
  !(Number(lat) === 0 && Number(lng) === 0) &&
  Number(lat) >= -85 &&
  Number(lat) <= 85 &&
  Number(lng) >= -180 &&
  Number(lng) <= 180;
const median = (nums) => {
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
// Degrees from the cluster median beyond which a point is treated as bad data
// and excluded from the fit (~550 km — far larger than any single region's
// spread, far smaller than an inter-continental jump).
const MAX_FIT_DEGREES = 5;

const MapHandler = memo(({ stops, center, active }) => {
  const map = useMap();
  const dispatch = useDispatch();
  const shouldFitbounds = useSelector((state) => state.common.shouldFitbounds);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (stops?.length > 0 && shouldFitbounds) {
      let coords = stops
        .filter((s) => plausibleLatLng(s.latitude, s.longitude))
        .map((s) => ({ lat: Number(s.latitude), lng: Number(s.longitude) }));
      // Drop outliers far from the median so a single bad coord can't zoom the
      // map out to the whole world (keeps the fit on the real cluster).
      if (coords.length > 2) {
        const mLat = median(coords.map((c) => c.lat));
        const mLng = median(coords.map((c) => c.lng));
        const within = coords.filter(
          (c) => Math.abs(c.lat - mLat) <= MAX_FIT_DEGREES && Math.abs(c.lng - mLng) <= MAX_FIT_DEGREES,
        );
        if (within.length > 0) coords = within;
      }
      const bounds = new window.google.maps.LatLngBounds();
      let any = false;
      coords.forEach((c) => {
        bounds.extend(new window.google.maps.LatLng(c.lat, c.lng));
        any = true;
      });
      if (any) map.fitBounds(bounds);
      dispatch(setShouldFitbounds(false));
    }
  }, [stops, map, shouldFitbounds]);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (center?.lat && center?.lng) {
      map.panTo(new window.google.maps.LatLng(center.lat, center.lng));
    }
  }, [center, map]);

  // Clicking a stop on the timeline pans the map so its pin is visible.
  // This existed but was dead: `active` was missing from the effect deps (it
  // only ever fired on `center` changes) and it fed panToBounds() a LatLng
  // (that API takes a LatLngBounds). Rules: resolve merged pins via activeIds
  // (a co-located "1·4·7" pin stands for several stop ids), pan only when the
  // pin is OUTSIDE the current viewport (a visible pin shouldn't move the
  // map), and pan at most once per selected stop (markers get a fresh array
  // identity on every render — without the ref, any background refresh would
  // yank the map back while the user is panning around).
  const lastPannedActiveId = useRef(null);
  useEffect(() => {
    if (!map || !active?.id) return;
    if (lastPannedActiveId.current === active.id) return;
    const stop = stops?.find((item) => item.id === active.id || item.activeIds?.includes(active.id));
    if (!stop || !plausibleLatLng(stop.latitude, stop.longitude)) return;
    lastPannedActiveId.current = active.id;
    const pos = new window.google.maps.LatLng(Number(stop.latitude), Number(stop.longitude));
    const bounds = map.getBounds();
    if (!bounds || !bounds.contains(pos)) {
      map.panTo(pos);
    }
  }, [active, stops, map]);

  return null;
});

const GoogleMap = memo(({ markers, polylines, center, active, disableInfoWindow = false }) => {
  const isActive = useCallback(
    (item) => {
      if (!active) return false;
      // A merged pin stands for several ids (its co-located stops + folded
      // start/end); light it up when any of them is the selected one.
      return item.id === active.id || item.activeIds?.includes(active.id);
    },
    [active],
  );

  const renderMarkers = useCallback(
    (items) => {
      // Drop any marker without a usable coordinate — one NaN would otherwise
      // crash the entire map (see validLatLng note above).
      return items
        .filter((item) => validLatLng(item.latitude, item.longitude))
        .map((item) => {
          return (
            <Marker
              key={item.id}
              position={{
                lat: item.latitude,
                lng: item.longitude,
              }}
              className={isActive(item) ? 'm-active' : ''}
              active={isActive(item)}
              icon={item.content}
              item={item}
              onToggle={item.onClick}
              items={items}
              disableInfoWindow={disableInfoWindow}
            />
          );
        });
    },
    [markers, isActive, disableInfoWindow],
  );
  return (
    <div className="c-map">
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY}>
        <GMap
          mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
          defaultCenter={{ lat: 46.2048821, lng: 6.1018395 }}
          defaultZoom={12}
          disableDefaultUI
        >
          {markers?.length > 0 && renderMarkers(markers, 'stop')}
          {polylines && polylines.map((polyline, i) => <Polyline key={`route-${i}`} route={polyline} />)}
        </GMap>
        <MapHandler stops={markers} center={center} active={active} />
      </APIProvider>
    </div>
  );
});

export default GoogleMap;
